import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { IconBell } from '@/components/icons'
import { toast } from '@/lib/toast'
import { notificationsApi, useNotifications } from '@/features/notifications/useNotifications'
import { formatDateTime } from '@/features/orders/status'

/** Καμπανάκι με μετρητή αδιάβαστων και πτυσσόμενη λίστα (§11). */
export function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const { notifications, unread, connected } = useNotifications()

  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success(t('notifications.allRead'))
    },
  })
  const markOne = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-label={t('notifications.title')}
        className="relative flex size-11 items-center justify-center rounded-xl text-white/90 transition-colors hover:bg-white/15"
      >
        <IconBell size={21} />
        {unread > 0 && (
          <span className="absolute right-1 top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-brand-700">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {/* Ένδειξη μόνο όταν χάθηκε η ζωντανή σύνδεση. Όσο όλα δουλεύουν δεν
            χρειάζεται σήμα: μια μόνιμη κουκκίδα απλώς προκαλεί απορία. */}
        {!connected && (
          <span
            aria-hidden
            className="absolute bottom-1.5 right-1.5 size-2 rounded-full bg-amber-400 ring-2 ring-brand-700"
          />
        )}
      </button>

      {open && (
        <>
          <button
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute right-0 z-20 mt-1 max-h-[70vh] w-80 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg">
            <header className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
              <span className="font-semibold text-gray-900">{t('notifications.title')}</span>
              {unread > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  className="text-xs font-medium text-brand-700 hover:underline"
                >
                  {t('notifications.markAll')}
                </button>
              )}
            </header>

            {!connected && (
              <p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
                {t('notifications.offline')}
              </p>
            )}

            {notifications.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                {t('notifications.empty')}
              </p>
            )}

            <ul className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    onClick={() => {
                      if (!notification.readAt) markOne.mutate(notification.id)
                      setOpen(false)
                      if (notification.data?.url) navigate(notification.data.url)
                    }}
                    className={
                      'block w-full px-4 py-3 text-left hover:bg-gray-50 ' +
                      (notification.readAt ? '' : 'bg-brand-50/50')
                    }
                  >
                    <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                    {notification.body && (
                      <p className="truncate text-xs text-gray-500">{notification.body}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      {formatDateTime(notification.createdAt)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
